import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send, X, MessageCircle, User, Clock } from 'lucide-react';

const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) 
  ? 'http://localhost:8001/api' 
  : '/api';

const AdminLiveChat = ({ user }) => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const inputRef = useRef(null);
  
  // Modal states
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  
  const showAlert = (message, type = 'info') => setAlertModal({ show: true, message, type });
  const showConfirm = (message, onConfirm) => setConfirmModal({ show: true, message, onConfirm });

  // Notification sound (simple beep)
  const notificationSound = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZSA0PVKzn77BdGAg+ltzy0H4pBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBSl+zPLaizsIGGS57OihUBELTqXh8bllHAU2jdXzz3wqBQ==";

  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    fetchChats();
    
    // Poll for new chats and unread messages
    pollingIntervalRef.current = setInterval(() => {
      fetchChats();
      if (selectedChat) {
        fetchMessages(selectedChat.id);
      }
    }, 3000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [selectedChat]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Only scroll if we're already near the bottom or if messages just loaded
    if (messages.length > 0) {
      const container = messagesEndRef.current?.parentElement;
      if (container) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        if (isNearBottom || messages.length === 1) {
          scrollToBottom();
        }
      }
    }
  }, [messages.length]);

  const fetchChats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/live-chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newChats = response.data || [];
      
      // Check for new unread messages
      const oldChats = chats;
      const hasNewUnread = newChats.some(chat => {
        const oldChat = oldChats.find(c => c.id === chat.id);
        return chat.unread_admin > (oldChat?.unread_admin || 0);
      });

      if (hasNewUnread && audioRef.current) {
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }

      setChats(newChats);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching chats:', err);
      setLoading(false);
    }
  };

  const fetchMessages = async (chatId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/live-chat/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  const selectChat = (chat) => {
    setSelectedChat(chat);
    fetchMessages(chat.id);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedChat || sending) return;

    const messageToSend = newMessage.trim();
    setSending(true);
    
    // Clear immediately
    setNewMessage('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/live-chat/${selectedChat.id}/messages`,
        { message: messageToSend },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Success - fetch
      await fetchMessages(selectedChat.id);
      await fetchChats();
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const closeChat = async (chatId) => {
    showConfirm('Bu sohbeti kapatmak istediğinizden emin misiniz?', async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/admin/live-chats/${chatId}/close`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        showAlert('Sohbet başarıyla kapatıldı.', 'success');
        setSelectedChat(null);
        await fetchChats();
      } catch (err) {
        console.error('Error closing chat:', err);
        showAlert('Sohbet kapatılamadı.', 'error');
      }
    });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Şimdi';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} saat önce`;
    return date.toLocaleDateString('tr-TR');
  };

  const totalUnread = chats.reduce((sum, chat) => sum + chat.unread_admin, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] flex bg-gray-900 rounded-lg overflow-hidden">
      {/* Chat List */}
      <div className="w-1/3 border-r border-gray-700 flex flex-col">
        <div className="p-4 bg-gray-800 border-b border-gray-700">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <MessageCircle className="text-blue-500" />
            Canlı Destek
            {totalUnread > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {totalUnread}
              </span>
            )}
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto">
          {chats.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MessageCircle className="mx-auto mb-3 text-gray-600" size={48} />
              <p>Henüz aktif sohbet yok</p>
            </div>
          ) : (
            chats.map((chat) => (
              <button
                key={chat.id}
                onClick={() => selectChat(chat)}
                className={`w-full p-4 border-b border-gray-700 hover:bg-gray-800 transition-colors text-left ${
                  selectedChat?.id === chat.id ? 'bg-gray-800' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <User className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="font-semibold text-white">{chat.user_name}</div>
                      <div className="text-xs text-gray-400">{chat.user_email}</div>
                    </div>
                  </div>
                  {chat.unread_admin > 0 && (
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                      {chat.unread_admin}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Clock size={12} />
                  {formatDate(chat.last_message_at)}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Window */}
      <div className="flex-1 flex flex-col">
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="p-4 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
              <div>
                <div className="font-bold text-white">{selectedChat.user_name}</div>
                <div className="text-sm text-gray-400">{selectedChat.user_email}</div>
              </div>
              <button
                onClick={() => closeChat(selectedChat.id)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
              >
                Sohbeti Kapat
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-900">
              {messages.map((msg) => {
                const isAdmin = msg.sender_role === 'ADMIN';
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        isAdmin
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : 'bg-gray-800 text-gray-100 rounded-bl-sm'
                      }`}
                    >
                      {!isAdmin && (
                        <div className="text-xs font-semibold mb-1 text-blue-400">
                          {msg.sender_name}
                        </div>
                      )}
                      <p className="break-words">{msg.message}</p>
                      <div className={`text-xs mt-1 ${
                        isAdmin ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {formatTime(msg.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 bg-gray-800 border-t border-gray-700">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Mesajınızı yazın..."
                  className="flex-1 bg-gray-700 text-white px-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white p-3 rounded-full transition-colors"
                >
                  <Send size={20} />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-900">
            <div className="text-center text-gray-500">
              <MessageCircle className="mx-auto mb-3 text-gray-600" size={64} />
              <p>Bir sohbet seçin</p>
            </div>
          </div>
        )}
      </div>

      {/* Site İçi Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">
                {alertModal.type === 'success' ? '✅' : alertModal.type === 'error' ? '❌' : 'ℹ️'}
              </div>
              <p className={`text-sm mb-4 whitespace-pre-line ${alertModal.type === 'success' ? 'text-green-400' : alertModal.type === 'error' ? 'text-red-400' : 'text-gray-300'}`}>
                {alertModal.message}
              </p>
              <button
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site İçi Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">⚠️</div>
              <p className="text-sm mb-4 text-gray-300">{confirmModal.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
                  className="flex-1 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    if (confirmModal.onConfirm) confirmModal.onConfirm();
                    setConfirmModal({ show: false, message: '', onConfirm: null });
                  }}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Evet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLiveChat;
